import { useEffect, useState, useCallback } from "react";

import { Editor } from "@tiptap/react";

import { useCaretBubbleStore } from "@demo/store/caret-bubble";
import { Role } from "@demo/types/base";

export interface UseEditorFocusProps {
  editor: Editor | null;
  editorRole: Role;
  onFocusChange?: (focused: boolean) => void;
}

export interface UseEditorFocusReturn {
  isFocused: boolean;
  focusEditor: () => void;
  blurEditor: () => void;
}

/**
 * Custom hook that encapsulates all focus-related logic for the editor.
 *
 * Features:
 * - Tracks focus state using editor events
 * - Manages focus transitions between editors
 * - Integrates with the caret bubble store for focus-based visibility
 *
 * @param props Configuration for the focus handler
 * @returns Focus state and control methods
 */
export function useEditorFocus({ editor, editorRole, onFocusChange }: UseEditorFocusProps): UseEditorFocusReturn {
  const [isFocused, setIsFocused] = useState(false);

  // Get caret bubble store actions
  const handleEditorFocus = useCaretBubbleStore((state) => state.handleEditorFocus);
  const handleEditorBlur = useCaretBubbleStore((state) => state.handleEditorBlur);

  // Focus control methods
  const focusEditor = useCallback(() => {
    if (editor && !editor.isDestroyed) {
      try {
        // Defer focus to avoid flushSync during render
        requestAnimationFrame(() => {
          if (editor && !editor.isDestroyed) {
            editor.commands.focus("end");
            setIsFocused(true);
          }
        });
      } catch (error) {
        console.warn(`[useEditorFocus] Failed to focus editor for ${editorRole}:`, error);
      }
    }
  }, [editor, editorRole]);

  const blurEditor = useCallback(() => {
    if (editor && !editor.isDestroyed) {
      try {
        // Defer blur to avoid flushSync during render
        requestAnimationFrame(() => {
          if (editor && !editor.isDestroyed) {
            editor.commands.blur();
            setIsFocused(false);
          }
        });
      } catch (error) {
        console.warn(`[useEditorFocus] Failed to blur editor for ${editorRole}:`, error);
      }
    }
  }, [editor, editorRole]);

  // Track editor focus state
  useEffect(() => {
    if (!editor) return;

    const handleFocus = () => {
      setIsFocused(true);
      onFocusChange?.(true);
      handleEditorFocus(editorRole);
    };

    const handleBlur = ({ event }: { event: FocusEvent }) => {
      setIsFocused(false);
      onFocusChange?.(false);
      handleEditorBlur(editorRole, event.relatedTarget as HTMLElement);
    };

    editor.on("focus", handleFocus);
    editor.on("blur", handleBlur);

    return () => {
      editor.off("focus", handleFocus);
      editor.off("blur", handleBlur);
    };
  }, [editor, editorRole, onFocusChange, handleEditorFocus, handleEditorBlur]);

  return {
    isFocused,
    focusEditor,
    blurEditor,
  };
}
